/* eslint-disable valid-typeof */
const type = (value, expected) => typeof(value) === expected;

/**
 * Checks if value is null.
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is null, else `false`.
 */
const isNull = (value) => value === null;

/**
 * Checks if value is undefined.
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is undefined, else `false`.
 */
const isUndefined = (value) => value === undefined;

/**
 * Checks if `value` is `null` or `undefined`.
 *
 * @param {*} value The value to check.
 * @returns {Boolean} Returns `true` if `value` is nullish, else `false`.
 * @example
 *
 * isNil(null);
 * // => true
 *
 * isNil(undefined);
 * // => true
 *
 * isNil(NaN);
 * // => false
 */
const isNil = (value) => isNull(value) || isUndefined(value);

/**
 * Checks if value type is an `object` but excludes Arrays and `null`
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value type is an object and it not Array or `null`, else `false`.
 * @example
 *
 * isObject({});
 * // => true
 *
 * isObject([]);
 * // => false
 *
 * isObject(null);
 * // => false
 *
 * isObject(new Date());
 * // => true
 *
 * isObject(/\s+/);
 * // => true
 *
 * isObject(() => {});
 * // => false
 */
const isObject = (value) => !isNull(value) && type(value, 'object') && !Array.isArray(value);

/**
 * Checks if value type is an `object` (including `arrays`), but excluding `null`
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value type is an object, else `false`.
 * @example
 *
 * isStrictObject({});
 * // => true
 *
 * isStrictObject([]);
 * // => true
 *
 * isStrictObject(new Date());
 * // => true
 *
 * isStrictObject(/\s+/);
 * // => true
 *
 * isStrictObject(() => {});
 * // => false
 *
 * isStrictObject(null);
 * // => false
 */
const isStrictObject = (value) => !isNull(value) && type(value, 'object');

/**
 * Checks if value is classified as an Array object.
 * @param {*} value The value to check.
 *
 * @return {Boolean} Returns `true` if value pass `Array.isArray` check, else `false`.
 * @example
 *
 * isArray({});
 * // => false
 *
 * isArray([]);
 * // => true
 */
const isArray = (value) => Array.isArray(value);

/**
 * Checks if value is classified as a String primitive.
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is a String primitive, else `false`.
 * @example
 *
 * isString('hello world');
 * // => true
 *
 * isString(String('hello world'));
 * // => true
 *
 *  isString(new String('hello world'));
 * // => true
 *
 * isString(String(null));
 * // => true
 *
 * isString(Object('hello world'));
 * // => false
 */
const isString = (value) => type(value, 'string') || value instanceof String;

/**
 * Checks if value type is a function.
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is correctly classified, else `false`.
 */
const isFunction = (value) => type(value, 'function');

/**
 * Checks if value type is a number.
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is correctly classified, else `false`.
 * @example
 *
 * isNumber(1);
 * // => true
 *
 * isNumber(NaN);
 * // => true
 *
 * isNumber(Number(null));
 * // => true
 *
 * isNumber(new Number(123));
 * // => true
 */
const isNumber = (value) => type(value, 'number') || value instanceof Number;

/**
 * Checks if value type is a boolean.
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is correctly classified, else `false`.
 * @example
 *
 * isBoolean(true);
 * // => true
 *
 * isBoolean(false);
 * // => true
 *
 * isBoolean(0);
 * // => false
 *
 * isBoolean(1);
 * // => false
 *
 * isBoolean(Boolean(0));
 * // => true
 *
 * isBoolean(Boolean(1));
 * // => true
 *
 * isBoolean(Boolean(null));
 * // => true
 */
const isBoolean = (value) => type(value, 'boolean') || value instanceof Boolean;

/**
 * Checks if value is an empty string
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is correctly classified, else `false`.
 * @example
 *
 * isEmptyString('');
 * // => true
 *
 * isEmptyString("");
 * // => true;
 *
 * isEmptyString(``);
 * // => true
 *
 * isEmptyString(String());
 * // => true
 *
 * isEmptyString(String(''));
 * // => true
 *
 * isEmptyString(String(undefined));
 * // => false
 */
const isEmptyString = (value) => isString(value) ? value.toString() === '' : false;

/**
 * Checks if value is an `isArray` and if is empty
 *
 * @see `isArray` for more details
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is correctly classified, else `false`.
 * @example
 *
 * isEmptyArray([]);
 * // => true
 *
 * isEmptyArray({});
 * // => false;
 *
 * isEmptyString(Array());
 * // => true
 */
const isEmptyArray = (value) => (isArray(value) ? !value.length : false);

/**
 * Checks if value is an object and if is empty
 *
 * @see `isObject` for more details
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is correctly classified, else `false`.
 * @example
 *
 * isEmptyObject({});
 * // => true
 *
 * isEmptyObject([]);
 * // => false;
 *
 * isEmptyObject(/\s+/);
 * // => true
 */
const isEmptyObject = (value) => (isObject(value) ? !Object.keys(value).length : false);

/**
 * Checks if value is an strict object and if is empty
 *
 * @see `isStrictObject` for more details
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is correctly classified, else `false`.
 * @example
 *
 * isStrictEmptyObject({});
 * // => true
 *
 * isStrictEmptyObject([]);
 * // => true;
 *
 * isStrictEmptyObject(/\s+/);
 * // => true
 */
const isStrictEmptyObject = (value) => (isStrictObject(value) ? !Object.keys(value).length : false);

/**
 * Checks if value matches either `isEmptyString`, `isNil`, `isEmptyArray` or `isEmptyObject`
 * It works differently that `lodash.isEmpty`, check below:
 *
 * @see `isEmptyString` for more details
 * @see `isNil` for more details
 * @see `isEmptyArray` for more details
 * @see `isEmptyObject` for more details
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is correctly classified, else `false`.
 * @example
 *
 * isEmpty('');
 * // => true
 *
 * isEmpty(String());
 * // => true
 *
 * isEmpty(null);
 * // => true
 *
 * isEmpty(undefined);
 * // => true
 *
 * isEmpty({});
 * // => true
 *
 * isEmpty([]);
 * // => true
 *
 * isEmpty(Symbol());
 * // => false
 *
 * isEmpty(false);
 * // => false
 *
 * isEmpty(true);
 * // => false;
 *
 * isEmpty(0);
 * // => false;
 *
 * isEmpty(NaN);
 * // => false
 *
 * @ref https://github.com/lodash/lodash/blob/master/test/isEmpty.js
 */
const isEmpty = (value) => isEmptyString(value) || isNil(value) || isEmptyArray(value) || isEmptyObject(value);

/**
 * Checks if value type is symbol.
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is Symbol, else `false`.
 */
const isSymbol = (value) => type(value, 'symbol');

/**
 * Checks if value type is bigint
 *
 * @param {*} value The value to check.
 * @return {Boolean} Returns `true` if value is BigInt, else `false`.
 */
const isBigInt = (value) => type(value, 'bigint');

module.exports = {
  isNull,
  isUndefined,
  isNil,
  isObject,
  isStrictObject,
  isArray,
  isString,
  isFunction,
  isNumber,
  isBoolean,
  isEmptyString,
  isEmptyArray,
  isEmptyObject,
  isStrictEmptyObject,
  isEmpty,
  isSymbol,
  isBigInt,
};
